import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { KILL_VERIFICATION_PHRASE } from '@/services/api/markets';

const MARKETS_PAGE_PATH = '/operations/markets';

const LINK_CLASSES = 'font-medium text-blue-500 underline underline-offset-2 hover:text-blue-400';

interface StageDialogProps {
  onCancel: () => void;
  onContinue: () => void;
}

/** Freeze flow stage 1 */
export const FreezeConfirmDialog = ({ onCancel, onContinue }: StageDialogProps) => (
  <Dialog onOpenChange={(open) => (open ? undefined : onCancel())} open>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Freeze Exchange</DialogTitle>
        <DialogDescription>
          {/* Deliberate fix: legacy said "accross" — corrected to "across" */}
          Are you sure you want to freeze the exchange? This will prevent all further trading activity across all markets.
        </DialogDescription>
      </DialogHeader>
      <p className="text-sm text-muted-foreground">
        Note: If you only need to suspend specific markets, you can do this from the{' '}
        <Link className={LINK_CLASSES} to={MARKETS_PAGE_PATH}>
          Markets page
        </Link>
        .
      </p>
      <DialogFooter>
        <Button onClick={onCancel} type="button" variant="outline">
          Cancel
        </Button>
        <Button className="bg-amber-500 text-white hover:bg-amber-600" onClick={onContinue} type="button">
          Continue
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
FreezeConfirmDialog.displayName = 'FreezeConfirmDialog';

/** Unfreeze flow stage 1 */
export const UnfreezeConfirmDialog = ({ onCancel, onContinue }: StageDialogProps) => (
  <Dialog onOpenChange={(open) => (open ? undefined : onCancel())} open>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Unfreeze Exchange</DialogTitle>
        <DialogDescription>Are you sure you want to unfreeze the exchange? This will resume all trading activity across all markets.</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button onClick={onCancel} type="button" variant="outline">
          Cancel
        </Button>
        <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={onContinue} type="button">
          Continue
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
UnfreezeConfirmDialog.displayName = 'UnfreezeConfirmDialog';

interface KillConfirmDialogProps extends StageDialogProps {
  /** "Freeze the exchange" alternative: closes this dialog and opens the freeze confirm */
  onSwitchToFreeze: () => void;
}

/** Killswitch flow stage 1 */
export const KillConfirmDialog = ({ onCancel, onContinue, onSwitchToFreeze }: KillConfirmDialogProps) => (
  <Dialog onOpenChange={(open) => (open ? undefined : onCancel())} open>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Kill Exchange</DialogTitle>
        <DialogDescription className="font-semibold text-red-500">WARNING: This action will completely shut down the exchange and cannot be undone immediately.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-3 text-sm text-muted-foreground">
        <p>Are you absolutely sure you want to kill the exchange? This is a critical action that will require significant time to recover from.</p>
        <p>Note: If you only need to temporarily suspend trading, consider these alternatives:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            You can{' '}
            <button className={LINK_CLASSES} onClick={onSwitchToFreeze} type="button">
              Freeze the exchange
            </button>{' '}
            instead, which allows for easier recovery
          </li>
          <li>
            You can suspend specific markets from the{' '}
            <Link className={LINK_CLASSES} to={MARKETS_PAGE_PATH}>
              Markets page
            </Link>
          </li>
        </ul>
      </div>
      <DialogFooter>
        <Button onClick={onCancel} type="button" variant="outline">
          Cancel
        </Button>
        <Button onClick={onContinue} type="button" variant="destructive">
          Continue
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
KillConfirmDialog.displayName = 'KillConfirmDialog';

/** Killswitch flow stage 2 — typed verification */
export const KillVerificationDialog = ({ onCancel, onContinue }: StageDialogProps) => {
  const inputId = useId();
  const [text, setText] = useState('');
  const isMatch = text === KILL_VERIFICATION_PHRASE;
  const showMismatch = text !== '' && !isMatch;

  const handleCancel = () => {
    setText('');
    onCancel();
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : handleCancel())} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verify Exchange Kill</DialogTitle>
          <DialogDescription>Final verification required.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">To proceed with killing the exchange, please type "{KILL_VERIFICATION_PHRASE}" in the field below:</p>
          <Label htmlFor={inputId}>Type '{KILL_VERIFICATION_PHRASE}'</Label>
          <Input autoComplete="off" id={inputId} onChange={(event) => setText(event.target.value)} value={text} />
          {showMismatch ? <p className="text-sm text-red-500">Text does not match</p> : null}
        </div>
        <DialogFooter>
          <Button onClick={handleCancel} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={!isMatch} onClick={onContinue} type="button" variant="destructive">
            Continue with Kill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
KillVerificationDialog.displayName = 'KillVerificationDialog';

/** Maintenance flow stage 1 — only when starting it; ending it goes straight to the justification */
export const StartMaintenanceConfirmDialog = ({ onCancel, onContinue }: StageDialogProps) => (
  <Dialog onOpenChange={(open) => (open ? undefined : onCancel())} open>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Start Exchange Maintenance</DialogTitle>
        <DialogDescription>
          Are you sure you want to start exchange maintenance? Every broker will be refused order, balance and account requests until maintenance is ended, and all brokers will be
          notified by webhook.
        </DialogDescription>
      </DialogHeader>
      <p className="text-sm text-muted-foreground">Only start maintenance once the exchange stop is planned — brokers stop sending orders as soon as they are notified.</p>
      <DialogFooter>
        <Button onClick={onCancel} type="button" variant="outline">
          Cancel
        </Button>
        <Button className="bg-amber-500 text-white hover:bg-amber-600" onClick={onContinue} type="button">
          Continue
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
StartMaintenanceConfirmDialog.displayName = 'StartMaintenanceConfirmDialog';
